import { inject, Injectable } from '@angular/core';
import { FormArray } from '@angular/forms';
import { FundStrategyModel, TotalsCompaniesNumberFS, TotalsFS } from '../pojo/fund-strategy-export-classes';
import { FsGroupFormGroup, SubGroupFormGroup } from '../pojo/fund-strategy-export-types';
import { DateService } from "../../../../util/common/date.service";
import {DatePipe, formatDate} from "@angular/common";

@Injectable({
  providedIn: 'root',
})
export class FundStrategyService {
  private datePipe = inject(DatePipe);



  calculateSubGroupTotals(subGroup: SubGroupFormGroup): void {
    const totals = {
      totalCompaniesNumber: 0,
      writeOffCompaniesNumber: 0,
      exitCompaniesNumber: 0,
      round: 0,
      investment: 0,
      proceeds: 0,
      gain: 0,
      moic: 0,
    };

    const roundsDataArray = subGroup.controls.roundsData;
    if (roundsDataArray && roundsDataArray.controls.length > 0) {
      // Get initial companies number from first round
      totals.totalCompaniesNumber = Number(roundsDataArray.value[0].companiesNumber);

      roundsDataArray.controls.forEach((round) => {
        totals.writeOffCompaniesNumber += Number(round.controls.writeOff.value);
        totals.exitCompaniesNumber += Number(round.controls.exitCompNumber.value);
        totals.round += Number(round.controls.round.value);

        const investment = Number(round.controls.investment.value);
        const companiesNumber = Number(round.controls.companiesNumber.value);
        totals.investment += investment * companiesNumber;

        const holding = Number(round.controls.holding.value) || 0;
        const exitCompNumber = Number(round.controls.exitCompNumber.value);
        const exitCompPrice = Number(round.controls.exitCompPrice.value);
        totals.proceeds += holding * exitCompNumber * exitCompPrice;
      });

      totals.gain = totals.proceeds - totals.investment;
      totals.moic = totals.investment !== 0 ? totals.proceeds / totals.investment : 0;
    }

    subGroup.controls.total.patchValue({
      totalCompaniesNumber: totals.totalCompaniesNumber,
      writeOffCompaniesNumber: totals.writeOffCompaniesNumber,
      exitCompaniesNumber: totals.exitCompaniesNumber,
      round: totals.round,
      investment: totals.investment,
      proceeds: totals.proceeds,
      gain: totals.gain,
      moic: totals.moic,
    });
  }

  /**
   * Calculates total values for the group
   */
  calculateSubTotals(grIndex: number, fs: FormArray<FsGroupFormGroup>): TotalsFS {
    const subGroupsArray = fs.controls[grIndex].controls.subGroups;
    const totals = new TotalsFS();
    subGroupsArray.controls.forEach((subGroup) => {
      const roundsData = subGroup.controls.roundsData.controls;
      if (roundsData && roundsData[0]) {
        totals.companiesNumber += Number(roundsData[0].controls.companiesNumber.value) || 0;
      }
      totals.writeOff += subGroup.value.total.writeOffCompaniesNumber;
      totals.exitCompNumber += subGroup.value.total.exitCompaniesNumber;
      totals.investment += subGroup.value.total.investment;
      totals.proceed += subGroup.value.total.proceeds;
    });

    totals.gain = totals.proceed - totals.investment;
    totals.moic = totals.proceed / totals.investment;

    return totals;
  }

  /**
   * Calculates total values for the model
   */
  calculateGrandTotal(subTotals?: TotalsFS[]): TotalsFS {
    const grandTotal = new TotalsFS();

    subTotals.forEach((subTotal) => {
      grandTotal.companiesNumber += subTotal.companiesNumber;
      grandTotal.writeOff += subTotal.writeOff;
      grandTotal.exitCompNumber += subTotal.exitCompNumber;
      grandTotal.investment += subTotal.investment;
      grandTotal.proceed += subTotal.proceed;
    });

    grandTotal.gain = grandTotal.proceed - grandTotal.investment;
    grandTotal.moic = grandTotal.proceed / grandTotal.investment;

    return grandTotal;
  }

  /**
   * Calculates total and exit companies numbers
   */
  calculateCompaniesNumbers(fs: FundStrategyModel): TotalsCompaniesNumberFS {
    const companiesNumbers = new TotalsCompaniesNumberFS();
    if (!fs || !fs.groups.length) {
      return companiesNumbers;
    }

    fs.groups.forEach((group) =>
      group.subGroups.forEach((sb) => {
          companiesNumbers.companiesNumber += sb.roundsData[0].companiesNumber || 0;
          let exit = 0;
          sb.roundsData.forEach((r) => {
            exit += r.exitCompNumber || 0;
          });
          companiesNumbers.exitCompNumber += exit;
        }
      ));
    return companiesNumbers;
  }


  setLastSaveText(fsModels: FundStrategyModel[]): FundStrategyModel[] {
    fsModels.forEach((model) => {
      model.lastSaveText = this.formatAndSetLastSaveText(model);
    });
    return this.sortStrategiesByLastUpdateDate(fsModels);
  }

  formatAndSetLastSaveText(model: FundStrategyModel): string {
    const createdDateFormatted = DateService.createUTCDate(model.createdDate.toString());
    const lastUpdateDateFormatted = DateService.createUTCDate(model.lastUpdateDate.toString());
    const isCreated = createdDateFormatted === lastUpdateDateFormatted;

    return (isCreated ? 'Created' : 'Updated')
      + (model.createdBy ? (' by ' + model.createdBy) : '') + ': '
      + this.datePipe.transform(lastUpdateDateFormatted, 'yyyy-MM-dd')
      + ' - '
      + this.datePipe.transform(lastUpdateDateFormatted, 'HH:mm');
  }



  /**
   * Sorts an array of FundStrategyModels by the lastUpdateDate property.
   * @param models An array of FundStrategyModels to sort.
   * @return A new array sorted by lastUpdateDate in descending order.
   */
  sortStrategiesByLastUpdateDate(models: FundStrategyModel[]): FundStrategyModel[] {
    return models.sort((a, b) => {
      const dateA = new Date(a.lastUpdateDate).getTime();
      const dateB = new Date(b.lastUpdateDate).getTime();
      return dateB - dateA;
    });
  }


  mapToFundStrategyModel = (response: FundStrategyModel): FundStrategyModel => {
    const lastSaveText = this.formatAndSetLastSaveText(response);
    return new FundStrategyModel(
      response.uuid,
      response.name,
      response.totalCommitment,
      response.expectedExpenses,
      response.carryInterestPcnt,
      response.orgUUID,
      response.groups,
      lastSaveText,
      response.lastUpdateDate,
      response.createdDate,
      response.createdBy);
  };
}

